/**
 * IT > Service Desk > one ticket (/it/service-desk/tickets/:id). The working
 * view: the request as the person wrote it, the conversation (public replies
 * and internal notes), the reply box, and the controls rail.
 */

import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { formatInstant } from '@ampersandbrands/ui';
import { Pill } from '@/components/Pill';
import { useReportPageState } from '@/lib/reportPageState';
import { useTicket } from '@/lib/useServiceDesk';
import { ReporterContact } from './ReporterContact';
import { SdShell } from './SdShell';
import { TicketComposer } from './TicketComposer';
import { TicketRail } from './TicketRail';
import { AttachmentList, TicketTimeline } from './TicketTimeline';
import { PRIORITY_LABELS, PRIORITY_TONES, STATUS_LABELS, STATUS_TONES } from './sdLabels';
import { SD_CARD, SD_MUTED } from './sdStyles';

export function Ticket() {
  const { id } = useParams();
  const ticketId = Number(id);
  const q = useTicket(ticketId);
  const t = q.data;

  // Lets "Report a problem with this page" say which ticket was on screen.
  const key = t?.display_key;
  useReportPageState(useMemo(() => (key ? [{ label: 'Ticket', value: key }] : []), [key]));

  return (
    <SdShell
      title={t ? `${t.display_key}  ${t.title}` : 'Ticket'}
      back={{ to: '/it/service-desk', label: 'Back to Service Desk' }}
    >
      {q.isLoading ? (
        <p className={`text-sm ${SD_MUTED}`}>Loading ticket...</p>
      ) : q.error || !t ? (
        <p className={`text-sm ${SD_MUTED}`}>
          This ticket could not be opened. It may have been moved, or you may not be allowed to see it.
        </p>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-2 text-[13px]">
            <Pill tone={STATUS_TONES[t.status]}>{STATUS_LABELS[t.status]}</Pill>
            <Pill tone={PRIORITY_TONES[t.priority]}>{PRIORITY_LABELS[t.priority]}</Pill>
            {t.hr_restricted && <Pill variant="outline">HR restricted</Pill>}
            <span className={SD_MUTED}>
              Opened by <ReporterContact name={t.reporter_name} email={t.reporter_email} fallback="unknown" />
              {t.store_no ? ` (store ${t.store_no})` : ''} on {formatInstant(t.created_at)}
            </span>
          </div>

          <div className="grid min-h-0 gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="flex min-w-0 flex-col gap-5">
              <section className={SD_CARD} aria-label="The request">
                <h2 className={`mb-2 text-[12px] font-semibold uppercase tracking-caps-pill ${SD_MUTED}`}>
                  The request
                </h2>
                <p className={`mb-2 text-[13px] ${SD_MUTED}`}>{t.category_path}</p>
                {t.description_html ? (
                  <div
                    className="amp-rich-text text-sm text-amp-ink dark:text-amp-dark-ink"
                    dangerouslySetInnerHTML={{ __html: t.description_html }}
                  />
                ) : (
                  <p className={`text-sm ${SD_MUTED}`}>No description.</p>
                )}
                {(t.form_display?.length ?? 0) > 0 && (
                  <dl className="mt-4 grid gap-x-4 gap-y-2 text-[13px] sm:grid-cols-[200px_minmax(0,1fr)]">
                    {t.form_display.map((row) => (
                      <div key={row.label} className="contents">
                        <dt className={`font-semibold ${SD_MUTED}`}>{row.label}</dt>
                        <dd className="text-amp-ink dark:text-amp-dark-ink">{row.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
                <AttachmentList items={t.attachments} />
              </section>

              <section aria-label="Conversation">
                <h2 className={`mb-2 text-[12px] font-semibold uppercase tracking-caps-pill ${SD_MUTED}`}>
                  Conversation
                </h2>
                <TicketTimeline comments={t.comments} events={t.events} />
              </section>

              <TicketComposer ticketId={t.id} />
            </div>

            <TicketRail ticket={t} />
          </div>
        </>
      )}
    </SdShell>
  );
}
